import { ROUTES } from '@reflex/shared/routes';

/**
 * True when `href` points into the host's instance-administration console.
 *
 * Notification payloads are authored by the server, and some of them carry
 * an operator deep link (`/admin/plugins/<name>`). The console is mounted
 * at the top level under `<AuthedShell>`, not under `orgs/:orgSlug`, so
 * every such link is a bare `/admin/...` path — matching the prefix is
 * enough, and an org-scoped path can never collide with it.
 */
export function isAdminHref(href: string): boolean {
  const admin = ROUTES.admin.root;
  return href === admin || href.startsWith(`${admin}/`);
}

/** The viewer facts that decide whether an admin-console link is followable. */
export interface AdminConsoleAccess {
  isPlatformAdmin: boolean;
  /**
   * `false` on a single-tenant self-host with auth disabled, where there
   * is no permission model and every affordance stays usable. Absent
   * means enabled. Same bypass the host's own `RequireSuperAdmin` and
   * `AdminShell` guards apply — without it the sole operator of an
   * auth-disabled install would be denied a console they can open.
   */
  authEnabled?: boolean;
}

/**
 * Whether a notification's call-to-action should be rendered for the
 * current viewer.
 *
 * A link into the admin console is only followable by a platform admin;
 * for anyone else it lands on the "access denied" screen. Rendering it
 * anyway offers a fix the reader cannot apply, which is worse than
 * offering none — so the button is dropped rather than disabled.
 */
export function canFollowNotificationHref(href: string, viewer: AdminConsoleAccess): boolean {
  return canOpenAdminConsole(viewer) || !isAdminHref(href);
}

/**
 * The one gate every instance-administration affordance in the inbox
 * consults: the renderer's operator body, the viewer-scoped heading in
 * `notificationHeadingFor`, and the detail panel's footer link.
 *
 * It is deliberately a question about the **viewer**, not about the
 * deployment, and that is what makes it correct everywhere:
 *
 * - An admin demoted after the row was written stops seeing operator
 *   detail on the next render, with no republish and no sweep.
 * - A dedicated single-tenant deployment is not a single-*org*
 *   deployment, and it also carries Runloop's own operators. No per-box
 *   flag can answer "should *this reader* see admin instructions"; only
 *   the reader can.
 * - An auth-disabled self-host has no memberships to carry the flag, and
 *   its sole operator can open the console — hence the `authEnabled`
 *   bypass, which mirrors the host's own `RequireSuperAdmin` /
 *   `AdminShell` guards. Keeping the same predicate means the inbox can
 *   never offer a console the guard will refuse, or withhold one it
 *   would allow.
 *
 * Note what this does *not* claim to fix. A viewer who holds
 * `isSuperAdmin` when they should not is a grant problem: on the server
 * that flag is an authz bypass threaded through every scoped handle
 * (`server/src/lib/authz.ts`), so an inbox row is the least of what it
 * buys them. This gate matches the console's answer; it is not a
 * substitute for the console's answer being right.
 */
export function canOpenAdminConsole(viewer: AdminConsoleAccess): boolean {
  return viewer.authEnabled === false || viewer.isPlatformAdmin;
}
